import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile,
  type User as FirebaseUser
} from 'firebase/auth';
import { deleteField, doc, getDoc, setDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, db } from '../lib/firebase';
import { clearPushToken } from '../lib/push';
import { clearPersistedCache } from '../lib/queryClient';
import {
  completeProfile as apiCompleteProfile,
  registerAppleCredential,
  updateProfile as apiUpdateProfile
} from '../lib/api';

// On native (Capacitor) the Firebase web popup flow does not work inside the
// WKWebView, so we drive the OS-level Google / Apple sheets via the native
// plugin and hand the resulting credential to the JS SDK. Everywhere else
// (browser, dev emulator) we keep the popup flow so there's a single auth
// instance, persistence model and emulator wiring across the app.
const isNative = Capacitor.isNativePlatform();

export interface ProfileInput {
  displayName: string;
  username: string;
  avatarTone?: number;
  /** Download URL of an uploaded profile photo, if the player added one. */
  photoURL?: string;
}

interface AuthValue {
  user: FirebaseUser | null;
  uid: string | null;
  /** True until the first auth-state resolution — gate the app on this. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Create the auth account only — the profile doc is written by completeProfile. */
  register: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  /** Finish the profile step for the signed-in user (after email or social sign-up). */
  completeProfile: (input: ProfileInput) => Promise<void>;
  /** Update an existing profile (name / avatar) without touching join date or completion.
   *  Pass `photoURL: null` to clear an uploaded photo (revert to the monogram). */
  updateUserProfile: (input: {
    displayName?: string;
    avatarTone?: number;
    photoURL?: string | null;
  }) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/** Write (merge) the player's public profile doc. The engine reads displayName from it. */
async function writeProfile(
  user: FirebaseUser,
  data: { displayName: string; username?: string; avatarTone?: number; profileComplete: boolean }
): Promise<void> {
  const ref = doc(db, 'users', user.uid);
  await setDoc(
    ref,
    {
      userId: user.uid,
      displayName: data.displayName,
      ...(data.username ? { username: data.username.toLowerCase() } : {}),
      ...(data.avatarTone != null ? { avatarTone: data.avatarTone } : {}),
      profileComplete: data.profileComplete,
      // Epoch ms to match the app-wide `Millis` convention (shared User.createdAt).
      // Not serverTimestamp(): that writes a Firestore Timestamp, which breaks numeric
      // time comparisons and renders as "Invalid Date" in the admin dashboard.
      createdAt: Date.now(),
      friendIds: []
    },
    { merge: true }
  );
}

/**
 * Make sure a brand-new sign-in has a profile doc. Existing docs are left untouched
 * (so we never clobber a finished profile). A freshly created doc is marked
 * `profileComplete: false` so the app routes the player to the profile step.
 */
async function ensureUserDoc(user: FirebaseUser): Promise<void> {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await writeProfile(user, {
    displayName: user.displayName ?? 'Player',
    profileComplete: false
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      uid: user?.uid ?? null,
      loading,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      async register(email, password) {
        await createUserWithEmailAndPassword(auth, email, password);
        // No profile doc yet — the app routes to the profile step (completeProfile).
      },
      async signInWithGoogle() {
        if (isNative) {
          const { credential } = await FirebaseAuthentication.signInWithGoogle();
          if (!credential?.idToken) throw new Error('No Google credential returned.');
          const oauth = GoogleAuthProvider.credential(credential.idToken, credential.accessToken);
          const cred = await signInWithCredential(auth, oauth);
          await ensureUserDoc(cred.user);
          return;
        }
        const cred = await signInWithPopup(auth, new GoogleAuthProvider());
        await ensureUserDoc(cred.user);
      },
      async signInWithApple() {
        if (isNative) {
          // The plugin generates and hashes the nonce natively, then returns the
          // raw nonce so the JS SDK can verify Apple's identity token.
          const { credential } = await FirebaseAuthentication.signInWithApple({
            scopes: ['email', 'name']
          });
          if (!credential?.idToken) throw new Error('No Apple credential returned.');
          const oauth = new OAuthProvider('apple.com').credential({
            idToken: credential.idToken,
            rawNonce: credential.nonce
          });
          const cred = await signInWithCredential(auth, oauth);
          await ensureUserDoc(cred.user);
          // Hand the one-time authorization code to the server so account deletion can
          // revoke the Apple token (Guideline 5.1.1(v)). Best-effort — never block sign-in.
          if (credential.authorizationCode) {
            await registerAppleCredential(credential.authorizationCode).catch(() => undefined);
          }
          return;
        }
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const cred = await signInWithPopup(auth, provider);
        await ensureUserDoc(cred.user);
      },
      async completeProfile({ displayName, username, avatarTone, photoURL }) {
        if (!auth.currentUser) throw new Error('Not signed in');
        const name = displayName.trim() || 'Player';
        // Username uniqueness is enforced server-side: the callable claims the
        // handle in a transaction and writes the profile doc with the Admin SDK
        // (the client can't write the username field — see firestore.rules).
        await apiCompleteProfile({ displayName: name, username, avatarTone, photoURL });
        // Mirror name + photo onto the Firebase Auth record (used by some providers/UI).
        await updateProfile(auth.currentUser, {
          displayName: name,
          ...(photoURL ? { photoURL } : {})
        }).catch(() => undefined);
      },
      async updateUserProfile({ displayName, avatarTone, photoURL }) {
        if (!auth.currentUser) throw new Error('Not signed in');
        // The display name is public free text, so it's moderated + written by the
        // updateProfile callable (the client can't write `displayName` directly —
        // see firestore.rules). avatarTone / photoURL stay direct, owner-scoped writes.
        if (displayName != null) {
          const name = displayName.trim() || 'Player';
          await apiUpdateProfile({ displayName: name });
          await updateProfile(auth.currentUser, { displayName: name }).catch(() => undefined);
        }
        const updates: Record<string, unknown> = {};
        if (avatarTone != null) updates.avatarTone = avatarTone;
        if (photoURL !== undefined) {
          // `null` clears the photo (revert to monogram); a string sets it.
          updates.photoURL = photoURL === null ? deleteField() : photoURL;
          await updateProfile(auth.currentUser, { photoURL: photoURL ?? '' }).catch(() => undefined);
        }
        if (Object.keys(updates).length === 0) return;
        // Merge so we never overwrite createdAt / username / profileComplete / displayName.
        await setDoc(doc(db, 'users', auth.currentUser.uid), updates, { merge: true });
      },
      async sendPasswordReset(email) {
        await sendPasswordResetEmail(auth, email);
      },
      async signOut() {
        // Drop this device's push token while still authenticated (the unregister
        // callable needs auth), so a signed-out device stops receiving pushes.
        await clearPushToken();
        // Clear the native provider session too, so the next sign-in re-prompts
        // for an account instead of silently reusing the last one.
        if (isNative) {
          await FirebaseAuthentication.signOut().catch(() => undefined);
        }
        await fbSignOut(auth);
        // Wipe this device's cached/persisted reads so the next account never sees
        // the previous player's data (entries are owner-private).
        await clearPersistedCache();
      }
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
